import { Checkbox, Col, Form, message, Modal, Row, Spin } from 'antd';
import React from 'react';
import { ITableRef } from '@/components/ZTable';
import { useTranslation } from 'react-i18next';
import { formatApiDateTime, ModalWidth, ROW_GUTTER, toNoTimeZoneDate } from '@/config';
import { SaveOutlined } from '@ant-design/icons';
import './styles.less';
import AppDateTimePicker from '@/components/FormItems/DateTimePicker';
import moment from 'moment';
import { TripDetailDto, RouteStopDto, TripStopModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
type Props = {
  onClose: (reload: boolean) => void;
  tripDetail: TripDetailDto;
};

interface SelectableRouteStop extends RouteStopDto {
  selected: boolean;
}
export default function UpdateTripDetailModal({ onClose, tripDetail }: Props) {
  const { t } = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [loading, setLoading] = React.useState(false);
  const [stops, setStops] = React.useState<SelectableRouteStop[]>([]);

  React.useEffect(() => {
    if (tripDetail?.id) {
      setLoading(true);

      apiSdk.TripDetailService.getEntityForUpdate({ tripDetailId: tripDetail.id }).then(trip => {
        apiSdk.RouteService.getRouteStops({ routeId: trip.routeId || '' })
          .then(stopResponse => {
            setStops((stopResponse as RouteStopDto[]).map(x => ({ ...x, selected: !!trip.tripDetailStops?.some(s => s.routeStopId === x.id) })));
            let formValues = {};
            trip.tripDetailStops?.forEach(stop => {
              formValues[`${stop.routeStopId}.isConnectionPoint`] = stop.isConnectionPoint;
              formValues[`${stop.routeStopId}.plannedArrivalDateTime`] = toNoTimeZoneDate(stop.plannedArrivalDateTime);
              formValues[`${stop.routeStopId}.plannedDepartureDateTime`] = toNoTimeZoneDate(stop.plannedDepartureDateTime);
            });
            formValues['plannedDepartureDateTime'] = toNoTimeZoneDate(trip.plannedDepartureDateTime);
            formValues['plannedArrivalDateTime'] = toNoTimeZoneDate(trip.plannedArrivalDateTime);
            form.setFieldsValue(formValues);
          })
          .finally(() => {
            setLoading(false);
          });
      });
    } else {
      setStops([]);
    }
  }, [tripDetail, form]);

  const onSubmit = values => {
    setLoading(true);
    let tripStops = stops
      .filter(x => x.selected)
      .map(stop => {
        return {
          isConnectionPoint: values[`${stop.id}.isConnectionPoint`],
          plannedArrivalDateTime: formatApiDateTime(values[`${stop.id}.plannedArrivalDateTime`]),
          plannedDepartureDateTime: formatApiDateTime(values[`${stop.id}.plannedDepartureDateTime`]),
          routeStopId: stop.id,
          stationId: stop.stationId,
        } as TripStopModel;
      });
    apiSdk.TripDetailService.updateTripDetail({
      body: {
        id: tripDetail.id || '',
        plannedArrivalDatetime: formatApiDateTime(values.plannedArrivalDateTime),
        plannedDepartureDatetime: formatApiDateTime(values.plannedDepartureDateTime),
        tripStops: tripStops,
      },
    })
      .then(response => {
        if (response?.success) {
          zTb.current?.reload();
          onClose(true);
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Modal
      style={{ top: 10 }}
      open={true}
      maskClosable={false}
      width={ModalWidth.medium}
      className="nta-custom-form edit-edit-trip-modal"
      title={`${tripDetail.tripName} / ${tripDetail.tripRefNumber}`}
      forceRender={true}
      okText={t('save')}
      cancelText={t('cancel')}
      okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
      onOk={() => {
        form.validateFields().then(onSubmit);
      }}
      onCancel={() => {
        onClose(false);
      }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical">
          <Row gutter={ROW_GUTTER} className="components-row-header">
            <Col md={7}>{t('tripsPage_Source')}</Col>
            <Col md={6}>{t('tripsPage_ArrivalDateTime')}</Col>
            <Col md={6}>{t('tripsPage_DepartureDateTime')}</Col>
            <Col md={5}>{t('tripsPage_ConnectionPoint')}</Col>
          </Row>
          <Row gutter={ROW_GUTTER} className="components-row-item">
            <Col md={13}>
              <b>{tripDetail?.sourceName}</b>
            </Col>

            <Col md={6}>
              <Form.Item name="plannedDepartureDateTime" rules={[{ required: true, message: '' }]}>
                <AppDateTimePicker disabledDate={x => x.isBefore(moment())} className="w-100" />
              </Form.Item>
            </Col>
            <Col md={5} style={{ textAlign: 'center' }}></Col>
          </Row>

          {stops.map(stop => {
            return (
              <Row key={stop.id} gutter={ROW_GUTTER} className="components-row-item" style={{ lineHeight: '47px' }}>
                <Col md={7}>
                  <Checkbox
                    checked={stop.selected}
                    onChange={e => {
                      let _stop = stops.find(x => x.id === stop.id);
                      if (_stop) {
                        _stop.selected = e.target.checked;
                        setStops([...stops]);
                      }
                    }}
                  >
                    <b>{stop.stationName}</b>
                  </Checkbox>
                </Col>
                {stop.selected ? (
                  <>
                    <Col md={6}>
                      <Form.Item name={`${stop.id}.plannedArrivalDateTime`} rules={[{ required: true, message: '' }]}>
                        <AppDateTimePicker disabledDate={x => x.isBefore(moment())} className="w-100" />
                      </Form.Item>
                    </Col>
                    <Col md={6}>
                      <Form.Item name={`${stop.id}.plannedDepartureDateTime`} rules={[{ required: true, message: '' }]}>
                        <AppDateTimePicker disabledDate={x => x.isBefore(moment())} className="w-100" />
                      </Form.Item>
                    </Col>
                    <Col md={5} style={{ textAlign: 'center' }}>
                      <Form.Item name={`${stop.id}.isConnectionPoint`} valuePropName="checked">
                        <Checkbox />
                      </Form.Item>
                    </Col>
                  </>
                ) : null}
              </Row>
            );
          })}
          <Row gutter={ROW_GUTTER} className="components-row-item">
            <Col md={7}>
              <b>{tripDetail?.destinationName}</b>
            </Col>
            <Col md={6}>
              <Form.Item name="plannedArrivalDateTime" rules={[{ required: true, message: '' }]}>
                <AppDateTimePicker disabledDate={x => x.isBefore(moment())} className="w-100" />
              </Form.Item>
            </Col>
            <Col md={6}></Col>
            <Col md={5} style={{ textAlign: 'center' }}></Col>
          </Row>
        </Form>
      </Spin>
    </Modal>
  );
}
